import { useToast } from '@chakra-ui/react';
import { TRPCClientError } from '@trpc/client';
import { useEffect, useMemo, useRef } from 'react';
import { EditorSavingStateEnum, dukeTTEditorState } from './dukeTTEditorState';

// Stable toast id so a stale tab that keeps trying to save re-uses the one
// "refresh to continue" toast instead of stacking a new one on every attempt.
const CONFLICT_TOAST_ID = 'autosave-conflict';

// The save indicator's anxiety came from loud transitions firing far more often
// than they needed to: "Saving..." flashing on every few-second autosave and the
// status bouncing to "Unsaved" on every keystroke. The fix (per best-in-class
// editors) is to debounce the VISIBLE state, not just the save:
//   - Don't show "Saving..." unless a save has actually been in flight this long.
//     Most saves finish faster, so the spinner never appears for the common case.
const SAVING_VISIBLE_DELAY_MS = 500;
//   - Don't drop to "Unsaved changes" the instant a key is pressed; only show it
//     if edits have been pending (unsaved) for longer than this, so quick edits
//     that save promptly never trip it and the resting state stays a calm "Saved".
const UNSAVED_VISIBLE_DELAY_MS = 1000;

/**
 * Shared autosave feedback for the content editors (flow editor and unit-guide
 * editor), so both report saving consistently: they drive the Navbar save-state
 * indicator and surface the same toasts.
 *
 * `entityLabel` names the thing being edited (e.g. "conversation", "unit guide")
 * so the conflict message reads naturally in each editor.
 */
export function useAutosaveFeedback(entityLabel: string) {
  const toast = useToast();

  // Latched once a save is rejected as stale. Recovery is a page refresh, so
  // until then every further autosave would conflict again; callers read this
  // to stop re-firing doomed saves (which otherwise produced dozens of
  // identical conflicts per stale tab). Cleared on any successful save.
  const hasConflict = useRef(false);

  // Pending timers for the deferred visible transitions to SAVING / UNSAVED.
  // Held in refs so every method can cancel them, keeping the indicator calm.
  const savingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsavedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear any pending visible-transition timer on unmount so it can't fire after
  // the editor is gone.
  useEffect(() => {
    return () => {
      if (savingTimer.current) {
        clearTimeout(savingTimer.current);
      }
      if (unsavedTimer.current) {
        clearTimeout(unsavedTimer.current);
      }
    };
  }, []);

  // Return a stable object so consumers can use it as an effect dependency
  // without the effect re-running every render. The flow editor subscribes to
  // the dirty signal in an effect that lists this object in its deps; a fresh
  // object each render tore that subscription down and rebuilt it on every
  // render, and because an edit itself re-renders, the live listener was
  // unsubscribed before valtio delivered its (async) change notification, so the
  // edit never reached the autosave or the "Unsaved changes" indicator. The
  // methods only close over `toast`, `entityLabel` and the stable refs, so
  // memoise on those.
  return useMemo(() => {
    const clearSavingTimer = () => {
      if (savingTimer.current) {
        clearTimeout(savingTimer.current);
        savingTimer.current = null;
      }
    };
    const clearUnsavedTimer = () => {
      if (unsavedTimer.current) {
        clearTimeout(unsavedTimer.current);
        unsavedTimer.current = null;
      }
    };

    return {
      hasConflict() {
        return hasConflict.current;
      },

      /**
       * There are pending edits. Do NOT flip the indicator immediately: a
       * keystroke that saves promptly should never show "Unsaved changes". Only
       * surface it if edits stay unsaved past the grace window. If a save starts
       * or completes first, that path cancels this pending transition.
       */
      onUnsaved() {
        if (unsavedTimer.current) {
          return;
        }
        unsavedTimer.current = setTimeout(() => {
          unsavedTimer.current = null;
          // Don't override an in-flight "Saving..." with "Unsaved".
          if (dukeTTEditorState.state !== EditorSavingStateEnum.SAVING) {
            dukeTTEditorState.state = EditorSavingStateEnum.UNSAVED_CHANGES;
          }
        }, UNSAVED_VISIBLE_DELAY_MS);
      },

      /**
       * A save is in flight. Don't show the spinner unless the save is actually
       * slow (most finish well under the delay), so the indicator doesn't flash
       * "Saving..." on every routine autosave.
       */
      onSaving() {
        clearUnsavedTimer();
        if (savingTimer.current) {
          return;
        }
        savingTimer.current = setTimeout(() => {
          savingTimer.current = null;
          dukeTTEditorState.state = EditorSavingStateEnum.SAVING;
        }, SAVING_VISIBLE_DELAY_MS);
      },

      /**
       * Settle the indicator back to "Saved" without a toast. Used when an edit
       * is abandoned (e.g. a failed rename whose value we reverted) and the
       * editor is otherwise clean, so leaving "Unsaved Changes" would be wrong.
       */
      onIdle() {
        clearSavingTimer();
        clearUnsavedTimer();
        dukeTTEditorState.state = EditorSavingStateEnum.SAVED;
      },

      onSaved() {
        hasConflict.current = false;
        // Cancel any pending "Saving..."/"Unsaved" transition: the save finished
        // (usually before the spinner was due), so the indicator settles straight
        // to a calm, steady "Saved" without ever flashing. A routine autosave only
        // updates this persistent indicator; toasts are reserved for the
        // exceptional path (failures and conflicts) in onSaveError.
        clearSavingTimer();
        clearUnsavedTimer();
        dukeTTEditorState.state = EditorSavingStateEnum.SAVED;
      },

      /**
       * Handle a failed save. Returns whether it was a stale-overwrite conflict so
       * the caller can decide on recovery (we never advance past a conflict; a
       * refresh is the recovery path). Leaves the indicator on "Unsaved Changes".
       *
       * On a conflict it latches `hasConflict` and uses a stable toast id, so a
       * stale tab that keeps editing shows one standing "refresh to continue"
       * toast rather than a new one per attempt.
       */
      onSaveError(error: unknown): { isConflict: boolean } {
        clearSavingTimer();
        clearUnsavedTimer();
        dukeTTEditorState.state = EditorSavingStateEnum.UNSAVED_CHANGES;

        const isConflict = error instanceof TRPCClientError && error.data?.code === 'CONFLICT';

        if (isConflict) {
          hasConflict.current = true;
          if (!toast.isActive(CONFLICT_TOAST_ID)) {
            toast({
              id: CONFLICT_TOAST_ID,
              title: 'Changes not saved',
              description: `This ${entityLabel} was updated in another tab. Refresh the page to see the latest version and try again.`,
              status: 'error',
              duration: null,
              isClosable: true,
            });
          }
        } else {
          toast({
            title: 'Changes not saved',
            description: `An error occurred while saving the ${entityLabel}. Please try again.`,
            status: 'error',
          });
        }

        return { isConflict };
      },
    };
  }, [toast, entityLabel]);
}
